import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';

import IntegrityIcon from '@mui/icons-material/VerifiedUser';
import ExcellenceIcon from '@mui/icons-material/EmojiEvents';
import RespectIcon from '@mui/icons-material/Diversity3';
import InnovationIcon from '@mui/icons-material/Lightbulb';
import CommitmentIcon from '@mui/icons-material/HandshakeOutlined';
import ResponsibilityIcon from '@mui/icons-material/AccountBalance';

const InstitutionalValues = () => {
  const sidebarSections = [
    {
      id: 'who-we-are',
      label: 'Quiénes Somos',
      path: '/about-us',
      children: [
        { id: 'history', label: 'Nuestra Historia', path: '/about-us/history' },
        { id: 'mission', label: 'Misión y Visión', path: '/about-us/mission-vision' },
        { id: 'values', label: 'Valores Institucionales', path: '/about-us/values' },
        { id: 'calendar', label: 'Calendario Académico', path: '/about-us/calendar' },
      ],
    },
    {
      id: 'services',
      label: 'Servicios',
      path: '/about-us/services',
    },
    {
      id: 'courses',
      label: 'Cursos',
      path: '/about-us/courses',
    },
    {
      id: 'library',
      label: 'Biblioteca',
      path: '/about-us/library',
    },
    {
      id: 'testimonials',
      label: 'Testimoniales',
      path: '/about-us/testimonials',
    },
    {
      id: 'regulations',
      label: 'Reglamentos',
      path: '/about-us/regulations',
    },
  ];

  const values = [
    {
      icon: <IntegrityIcon />,
      title: 'Integridad',
      description: 'Actuamos con honestidad, transparencia y ética en todas nuestras acciones, promoviendo una cultura de confianza y responsabilidad en nuestra comunidad educativa.',
      color: '#2B2B6B',
      principles: [
        'Honestidad en todas nuestras acciones',
        'Transparencia en procesos y decisiones',
        'Coherencia entre valores y acciones',
        'Rendición de cuentas',
      ],
    },
    {
      icon: <ExcellenceIcon />,
      title: 'Excelencia',
      description: 'Buscamos constantemente la calidad superior en nuestros programas académicos, servicios y procesos, manteniendo altos estándares que nos distinguen.',
      color: '#E31E24',
      principles: [
        'Calidad académica superior',
        'Mejora continua de procesos',
        'Innovación en metodologías',
        'Búsqueda de resultados sobresalientes',
      ],
    },
    {
      icon: <RespectIcon />,
      title: 'Respeto',
      description: 'Valoramos la diversidad, promovemos la inclusión y fomentamos un ambiente de convivencia armónica donde todas las personas son tratadas con dignidad.',
      color: '#00CED1',
      principles: [
        'Valoración de la diversidad',
        'Inclusión y equidad',
        'Escucha activa',
        'Convivencia armónica',
      ],
    },
    {
      icon: <InnovationIcon />,
      title: 'Innovación',
      description: 'Fomentamos la creatividad, el pensamiento crítico y la búsqueda de soluciones novedosas que respondan a los desafíos del mundo actual.',
      color: '#2B2B6B',
      principles: [
        'Pensamiento creativo',
        'Adaptación al cambio',
        'Uso de tecnologías emergentes',
        'Espíritu emprendedor',
      ],
    },
    {
      icon: <CommitmentIcon />,
      title: 'Compromiso',
      description: 'Nos dedicamos con pasión y responsabilidad al logro de nuestros objetivos institucionales y al éxito de nuestros estudiantes.',
      color: '#E31E24',
      principles: [
        'Dedicación al estudiante',
        'Cumplimiento de objetivos',
        'Perseverancia',
        'Responsabilidad compartida',
      ],
    },
    {
      icon: <ResponsibilityIcon />,
      title: 'Responsabilidad Social',
      description: 'Contribuimos activamente al desarrollo sostenible de nuestra sociedad, formando profesionales conscientes de su rol en la transformación social.',
      color: '#00CED1',
      principles: [
        'Conciencia social',
        'Desarrollo sostenible',
        'Servicio a la comunidad',
        'Impacto positivo',
      ],
    },
  ];

  return (
    <>
    
      <Container maxWidth="xl" sx={{ mb: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
          </Grid>

          <Grid item xs={12} md={9}>
            {/* Introduction */}
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  mb: 2,
                }}
              >
                Nuestros valores institucionales son los pilares fundamentales que orientan 
                nuestras decisiones, acciones y relaciones dentro de la comunidad de American College. 
                Estos principios nos definen como institución y guían el desarrollo integral de 
                nuestros estudiantes.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                }}
              >
                Cada miembro de nuestra comunidad educativa — estudiantes, docentes, personal 
                administrativo y colaboradores — está comprometido con vivir y promover estos valores 
                en el día a día.
              </Typography>
            </Box>

            {/* Values Grid */}
            <Grid container spacing={4}>
              {values.map((value, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      border: '1px solid #E0E0E0',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 12px 32px ${value.color}25`,
                        borderColor: value.color,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      {/* Icon and Title */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                          sx={{
                            backgroundColor: `${value.color}15`,
                            color: value.color,
                            width: 70,
                            height: 70,
                            mr: 2,
                          }}
                        >
                          {React.cloneElement(value.icon, { sx: { fontSize: 35 } })}
                        </Avatar>
                        <Typography
                          variant="h5"
                          sx={{
                            color: value.color,
                            fontWeight: 700,
                            fontSize: '1.4rem',
                          }}
                        >
                          {value.title}
                        </Typography>
                      </Box>

                      {/* Description */}
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#666',
                          lineHeight: 1.7,
                          mb: 3,
                        }}
                      >
                        {value.description}
                      </Typography>

                      {/* Principles */}
                      <Box
                        sx={{
                          background: `${value.color}05`,
                          borderRadius: 2,
                          p: 2.5,
                          borderLeft: `4px solid ${value.color}`,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: value.color,
                            fontWeight: 600,
                            mb: 1.5,
                            fontSize: '0.9rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Principios Clave
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                          {value.principles.map((principle, idx) => (
                            <Box
                              component="li"
                              key={idx}
                              sx={{
                                color: '#555',
                                fontSize: '0.95rem',
                                mb: 0.5,
                                lineHeight: 1.6,
                              }}
                            >
                              {principle}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Commitment Statement */}
            <Box
              sx={{
                mt: 5,
                p: 4,
                background: 'linear-gradient(135deg, #2B2B6B 0%, #1a1a4a 100%)',
                borderRadius: 2,
                color: 'white',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '1.3rem', md: '1.5rem' },
                }}
              >
                Nuestro Compromiso
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.05rem',
                  lineHeight: 1.8,
                  opacity: 0.95,
                  maxWidth: '800px',
                  mx: 'auto',
                }}
              >
                En American College, no solo enseñamos estos valores, sino que los vivimos 
                diariamente. Nos comprometemos a crear un ambiente donde cada estudiante 
                pueda desarrollar su potencial académico y personal, guiado por principios 
                éticos sólidos que lo prepararán para ser un profesional íntegro y un 
                ciudadano responsable.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default InstitutionalValues;